import React, { useState, useEffect, useCallback, useRef } from 'react';
import './OnlineNotification.css';

const AUTO_DISMISS_MS = 4000;
const MAX_VISIBLE     = 3;

const getRelativeTime = (timestamp) => {
  const diff = Math.floor((Date.now() - timestamp) / 1000);
  if (diff < 5)  return "À l'instant";
  if (diff < 60) return `Il y a ${diff}s`;
  return `Il y a ${Math.floor(diff / 60)}min`;
};

const OnlineNotification = ({ events = [], onNavigate }) => {
  const [visible, setVisible] = useState([]);
  const timerRefs = useRef({});
  const intervalRef = useRef(null);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    intervalRef.current = setInterval(() => forceUpdate((n) => n + 1), 10000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const dismiss = useCallback((id) => {
    setVisible((prev) => prev.filter((n) => n.id !== id));
    if (timerRefs.current[id]) {
      clearTimeout(timerRefs.current[id]);
      delete timerRefs.current[id];
    }
  }, []);

  useEffect(() => {
    if (!events.length) return;

    const latest = events[0];
    const id = `${latest.user_id}-${latest.timestamp}`;

    setVisible((prev) => {
      if (prev.find((n) => n.id === id)) return prev;
      return [{ id, ...latest }, ...prev].slice(0, MAX_VISIBLE);
    });

    timerRefs.current[id] = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
  }, [events, dismiss]);

  useEffect(() => () => {
    Object.values(timerRefs.current).forEach(clearTimeout);
  }, []);

  const handleNavigate = useCallback((section, id) => {
    dismiss(id);
    onNavigate?.(section);
  }, [dismiss, onNavigate]);

  if (!visible.length) return null;

  return (
    <div className="on-stack" role="status" aria-live="polite">
      {visible.map((n) => {
        const isSim    = n.type === 'simulation';
        const isOnline = n.online;

        const target = isSim ? 'secufireActionLogs' : 'userLogs';

        const cardMod =
          isSim ? 'on-card--sim'
          : isOnline ? 'on-card--online'
          : 'on-card--offline';

        const dotMod =
          isSim ? 'dot-sim'
          : isOnline ? 'dot-on'
          : 'dot-off';

        return (
          <div key={n.id} className={`on-card ${cardMod}`}>

            {/* Header */}
            <div className="on-header">
              <img src="/favicon.ico" alt="" className="on-favicon" />
              <span className="on-header-app">SecuFire Action Dashboard</span>
              <span className="on-header-time">
                {getRelativeTime(n.timestamp)}
              </span>
              <button
                className="on-close"
                onClick={() => dismiss(n.id)}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="on-body">
              <span className={`on-dot ${dotMod}`} />

              <div className="on-content">
                <div className="on-name">
                  {n.username || `Utilisateur ${n.user_id}`}
                </div>

                <div className={`on-msg ${
                  isSim ? 'on-msg--sim'
                  : isOnline ? 'on-msg--on'
                  : 'on-msg--off'
                }`}>
                  {isSim
                    ? (n.message || 'a lancé une simulation')
                    : isOnline
                      ? 'vient de se connecter'
                      : 'vient de se déconnecter'}
                </div>

                <div className="on-actions">

                  {/* ✅ BOUTON ACTIF */}
                  <button
                    className="on-btn on-btn--primary"
                    onClick={() => handleNavigate(target, n.id)}
                  >
                    {isSim
                      ? 'Voir logs SecuFire'
                      : 'Voir logs utilisateurs'}
                  </button>

                  <button
                    className="on-btn on-btn--ghost"
                    onClick={() => dismiss(n.id)}
                  >
                    Ignorer
                  </button>

                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div
              className={`on-progress ${
                isSim ? 'on-progress--sim'
                : isOnline ? 'on-progress--on'
                : 'on-progress--off'
              }`}
              style={{ animationDuration: `${AUTO_DISMISS_MS}ms` }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default OnlineNotification;